import {computed, onUnmounted, ref, watch} from 'vue'

import type {BellowsDirection} from '@/components/harmonika'
import {buttonMidis, PRECALC_DATA} from '@/components/harmonika'
import {startButtonTones} from '@/components/harmonika/buttonTone'
import {sameMembers} from '@/shared/sameMembers'

/** Zerlegt eine Knopf-ID `"row-btn"` sicher in ein Indexpaar (null bei Fehlformat). */
function parseButtonId(buttonId: string): [number, number] | null {
  const [rowIdx, btnIdx] = buttonId.split('-').map(Number)
  if (rowIdx === undefined || btnIdx === undefined || Number.isNaN(rowIdx) || Number.isNaN(btnIdx)) {
    return null
  }
  return [rowIdx, btnIdx]
}

function midisOf(buttonId: string, direction: BellowsDirection): number[] {
  const parsed = parseButtonId(buttonId)
  const button = parsed ? PRECALC_DATA[parsed[0]]?.buttons[parsed[1]] : undefined
  return button ? buttonMidis(button, direction) : []
}

/**
 * Halte-Logik des Simulators: welche Knöpfe gedrückt sind und dass sie klingen – auch über einen
 * Balgwechsel hinweg. Klang und Stimmung kommen aus `buttonTone.ts`; `activeMidiNotes` bleibt im
 * G-C-F-B-Raum der Knopftabelle, damit die Gleichton-Hinweise der Knöpfe stimmen.
 */
export function useHarmonikaAudio() {
  const direction = ref<BellowsDirection>('DRUCK')
  const activeButtons = ref<Set<string>>(new Set())
  const stopByButton = new Map<string, () => void>()

  const activeMidiNotes = computed<Set<number>>((oldValue) => {
    const next = new Set([...activeButtons.value].flatMap((buttonId) => midisOf(buttonId, direction.value)))
    return sameMembers(oldValue, next) ? oldValue : next
  })

  function sound(buttonId: string, currentDirection: BellowsDirection) {
    stopByButton.get(buttonId)?.()
    stopByButton.set(buttonId, startButtonTones(midisOf(buttonId, currentDirection)))
  }

  function silence(buttonId: string) {
    stopByButton.get(buttonId)?.()
    stopByButton.delete(buttonId)
  }

  function activateButton(rowIdx: number, btnIdx: number) {
    const buttonId = `${rowIdx}-${btnIdx}`
    if (activeButtons.value.has(buttonId)) return

    const nextActiveButtons = new Set(activeButtons.value)
    nextActiveButtons.add(buttonId)
    activeButtons.value = nextActiveButtons
    sound(buttonId, direction.value)
  }

  function deactivateButton(rowIdx: number, btnIdx: number) {
    const buttonId = `${rowIdx}-${btnIdx}`
    if (!activeButtons.value.has(buttonId)) return

    const nextActiveButtons = new Set(activeButtons.value)
    nextActiveButtons.delete(buttonId)
    activeButtons.value = nextActiveButtons
    silence(buttonId)
  }

  watch(direction, (nextDirection) => {
    activeButtons.value.forEach((buttonId) => sound(buttonId, nextDirection))
  })

  // Die Engine ist geteilt und bleibt offen – gehaltene Knöpfe müssen selbst verstummen.
  onUnmounted(() => {
    stopByButton.forEach((stop) => stop())
    stopByButton.clear()
  })

  return {
    direction,
    activeButtons,
    activeMidiNotes,
    activateButton,
    deactivateButton,
  }
}
